import { FlowEventType } from '@zola-helpers/client/dist/es';
import { v4 as uuidv4 } from 'uuid';
import React, { useState, ReactNode, useContext } from 'react';
import {
  UserContext,
  BusinessUnit,
  BusinessUnitComponent,
} from '@zola-helpers/client/dist/es/@types';
import { FlowEventConfig } from '@zola/tracking-contracts/src/configs';

export type OnboardingContextStateType = {
  business_unit_source?: BusinessUnit;
  business_unit_feature?: BusinessUnitComponent;
  section: number;
  sectionsLength: number;
  step: number;
  sectionStepsLength: number;
  isLogin: boolean;
  portalContainerRef?: React.RefObject<HTMLDivElement>;
  onboardingCompleted: boolean;
  /** Tracks the absolute step position in the flow, used in tracking */
  absoluteStepIndex: number;
  initialUserContext: UserContext;
  currentUserContext: UserContext;
};

const defaultState: OnboardingContextStateType = {
  section: 0,
  sectionsLength: 0,
  step: 0,
  sectionStepsLength: 0,
  isLogin: false,
  portalContainerRef: undefined,
  onboardingCompleted: false,
  absoluteStepIndex: 0,
  initialUserContext: {},
  currentUserContext: {},
};

type OnboardingContext = {
  onboardingState: OnboardingContextStateType;
  setOnboardingState: React.Dispatch<React.SetStateAction<OnboardingContextStateType>>;
  toggleIsLogin: () => void;
  /** @deprecated Legacy tracking, use flowTrackingContractsData */
  flowTrackingData?: FlowEventType;
  flowTrackingContractsData: FlowEventConfig;
  setFlowTrackingContractsData: React.Dispatch<React.SetStateAction<FlowEventConfig>>;
};

export const OnboardingContext = React.createContext<OnboardingContext>({
  onboardingState: defaultState,
  setOnboardingState: () => undefined,
  toggleIsLogin: () => undefined,
  flowTrackingContractsData: {} as FlowEventConfig,
  setFlowTrackingContractsData: () => undefined,
});

const { Provider } = OnboardingContext;

export const OnboardingProvider = ({
  children,
  flowTrackingData,
  flowTrackingContractsData,
  userContext,
}: {
  children: ReactNode;
  flowTrackingData?: Omit<FlowEventType, 'flow_run_id'>;
  flowTrackingContractsData: Omit<FlowEventConfig, 'flow_run_id'>;
  userContext: UserContext;
}): JSX.Element => {
  const [onboardingState, setOnboardingState] = useState({
    ...defaultState,
    initialUserContext: userContext,
    currentUserContext: userContext,
  });
  const [flowEventData] = useState(
    flowTrackingData ? { ...flowTrackingData, flow_run_id: uuidv4() } : undefined
  );
  const [flowEventContractsData, setFlowTrackingContractsData] = useState({
    ...flowTrackingContractsData,
    flow_run_id: uuidv4(),
  });

  const toggleIsLogin = () => {
    setOnboardingState({ ...onboardingState, isLogin: !onboardingState.isLogin });
  };

  return (
    <Provider
      value={{
        onboardingState,
        setOnboardingState,
        toggleIsLogin,
        flowTrackingData: flowEventData,
        flowTrackingContractsData: flowEventContractsData,
        setFlowTrackingContractsData,
      }}
    >
      {children}
    </Provider>
  );
};

export const useOnboardingContext = (): OnboardingContext => useContext(OnboardingContext);

export const ONBOARDING_PORTAL_CONTENT = 'onboarding-content-container';
